import React from 'react'
import clsx from 'clsx'
import { formatPercent } from '@/lib/dashboardFormat'

export type RankingItem = {
  id: string
  label: string
  /** Headline figure for the row, already formatted */
  primary: string
  secondary?: string
  pct: number
}

type RankingTone = 'positive' | 'negative' | 'neutral'

const toneBar: Record<RankingTone, string> = {
  positive: 'bg-[#16A34A] dark:bg-[#a1a1aa]',
  negative: 'bg-[#DC2626] dark:bg-[#6b6b70]',
  neutral: 'bg-[#2563EB] dark:bg-[#a1a1aa]',
}

interface RankingBarsProps {
  title: string
  description?: string
  items: RankingItem[]
  emptyMessage: string
  loading?: boolean
  tone?: RankingTone
  maxItems?: number
}

const RankingBars: React.FC<RankingBarsProps> = ({
  title,
  description,
  items,
  emptyMessage,
  loading = false,
  tone = 'neutral',
  maxItems = 6,
}) => {
  const visible = items.slice(0, maxItems)
  const hidden = Math.max(items.length - visible.length, 0)

  return (
    <div className="flex h-full flex-col gap-4 rounded-[20px] border border-border bg-card p-5 shadow-sm">
      <div className="flex flex-col gap-1">
        <h3 className="text-[15px] font-semibold text-foreground">{title}</h3>
        {description && (
          <p className="text-[12px] leading-snug text-muted-foreground">{description}</p>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col gap-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="mb-2 h-4 w-1/2 rounded bg-card-muted" />
              <div className="h-2 w-full rounded-full bg-card-muted" />
            </div>
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="flex flex-1 items-center justify-center py-8">
          <p className="max-w-[240px] text-center text-[13px] text-muted-foreground">
            {emptyMessage}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3.5">
          {visible.map((item) => (
            <div key={item.id} className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate text-[13px] font-medium text-foreground" title={item.label}>
                  {item.label}
                </span>
                <span className="flex shrink-0 items-baseline gap-2">
                  <span className="text-[13px] font-semibold text-foreground">{item.primary}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {formatPercent(item.pct)}
                  </span>
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-card-muted">
                <div
                  className={clsx('h-full rounded-full transition-all duration-500', toneBar[tone])}
                  style={{ width: `${Math.max(Math.min(item.pct, 100), item.pct > 0 ? 3 : 0)}%` }}
                />
              </div>
              {item.secondary && (
                <span className="text-[11px] text-muted-foreground">{item.secondary}</span>
              )}
            </div>
          ))}
          {hidden > 0 && (
            <p className="pt-1 text-[11px] text-muted-foreground">
              +{hidden} {hidden === 1 ? 'outro' : 'outros'} fora do top {maxItems}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default RankingBars
